import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Divider,
  Group,
  Paper,
  Popover,
  Title,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { IconAlertCircle, IconAlertTriangle, IconArrowLeft, IconDeviceFloppy, IconRocket } from '@tabler/icons-react';
import { Link } from '@tanstack/react-router';
import type { FlowVoiceConfig, LanguageConfig } from '@legacy-shared/widgets/core/types';
import { ErrorAlert } from '@/components/error-alert';
import type { FlowIssue } from '@/widgets/flow-validation';
import { FlowVersions } from './flow-versions';
import { FlowVoiceLanguageControl } from './flow-voice-language-control';
import { IssuesList } from './issues-list';

function prunedAtLoadMessage(count: number) {
  const plural = count !== 1 ? 's' : '';

  return `Cleaned up ${count} stored connection${plural} or action${plural} that ${plural ? 'were' : 'was'} out of date. Saving or publishing will make this permanent.`;
}

function WarningsBadge({
  issues,
  warningCount,
  onSelectNode,
}: {
  issues: FlowIssue[];
  warningCount: number;
  onSelectNode: (nodeId: string) => void;
}) {
  return (
    <Popover width={360} position="bottom-start" shadow="md" withArrow>
      <Popover.Target>
        <UnstyledButton aria-label={`View ${warningCount} warning${warningCount !== 1 ? 's' : ''}`}>
          <Badge size="xs" variant="light" color="yellow" leftSection={<IconAlertTriangle size={10} />}>
            {warningCount} Warning{warningCount !== 1 ? 's' : ''}
          </Badge>
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown p="sm">
        <IssuesList issues={issues} onSelectNode={onSelectNode} levelFilter="warning" />
      </Popover.Dropdown>
    </Popover>
  );
}

function ErrorsButton({
  issues,
  errorCount,
  onSelectNode,
}: {
  issues: FlowIssue[];
  errorCount: number;
  onSelectNode: (nodeId: string) => void;
}) {
  return (
    <Popover width={360} position="bottom-end" shadow="md" withArrow>
      <Popover.Target>
        <Button color="red" variant="light" leftSection={<IconAlertCircle size={16} />}>
          {errorCount} Error{errorCount !== 1 ? 's' : ''}
        </Button>
      </Popover.Target>
      <Popover.Dropdown p="sm">
        <IssuesList issues={issues} onSelectNode={onSelectNode} levelFilter="error" />
      </Popover.Dropdown>
    </Popover>
  );
}

type FlowEditorToolbarProps = {
  flowId: string;
  flowName: string;
  currentPublicationId: string | null;
  hasChanges: boolean;
  isSaving: boolean;
  isPublishing: boolean;
  actionError: string | null;
  prunedAtLoad: number;
  onSave: () => void;
  onPublish: () => void;
  voiceConfig: FlowVoiceConfig;
  onVoiceConfigChange: (config: FlowVoiceConfig) => void;
  languageConfig?: LanguageConfig;
  onLanguageConfigChange: (config: LanguageConfig | undefined) => void;
  canPublish: boolean;
  issues: FlowIssue[];
  errorCount: number;
  warningCount: number;
  onSelectNode: (nodeId: string) => void;
  elevenLabsAvailable: boolean;
};

export function FlowEditorToolbar({
  flowId,
  flowName,
  currentPublicationId,
  hasChanges,
  isSaving,
  isPublishing,
  actionError,
  prunedAtLoad,
  onSave,
  onPublish,
  voiceConfig,
  onVoiceConfigChange,
  languageConfig,
  onLanguageConfigChange,
  canPublish,
  issues,
  errorCount,
  warningCount,
  onSelectNode,
  elevenLabsAvailable,
}: FlowEditorToolbarProps) {
  const isDraft = currentPublicationId === null;

  return (
    <Paper
      px="md"
      py="xs"
      radius={0}
      withBorder={false}
      style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}
    >
      <ErrorAlert message={actionError} mb="sm" />
      {prunedAtLoad > 0 && (
        <Alert color="yellow" icon={<IconAlertTriangle size={16} />} mb="sm">
          {prunedAtLoadMessage(prunedAtLoad)}
        </Alert>
      )}
      <Group justify="space-between" wrap="nowrap">
        <Group gap="md" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
          <ActionIcon variant="subtle" renderRoot={(props) => <Link to="/flows" {...props} />}>
            <IconArrowLeft size={18} />
          </ActionIcon>
          <Divider orientation="vertical" />
          <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
            <Tooltip label={flowName} disabled={flowName.length <= 40} openDelay={400} withArrow>
              <Title order={4} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {flowName}
              </Title>
            </Tooltip>
            <Group gap={4} style={{ maxWidth: 200 }}>
              <Badge size="sm" variant="light" color={isDraft ? 'yellow' : 'green'}>
                {isDraft ? 'Draft' : 'Published'}
              </Badge>
              {hasChanges && (
                <Badge size="xs" variant="dot" color="orange">
                  Unsaved
                </Badge>
              )}
              {warningCount > 0 && (
                <WarningsBadge issues={issues} warningCount={warningCount} onSelectNode={onSelectNode} />
              )}
            </Group>
          </Group>
          <Divider orientation="vertical" />
          <FlowVoiceLanguageControl
            voiceConfig={voiceConfig}
            onVoiceConfigChange={onVoiceConfigChange}
            languageConfig={languageConfig}
            onLanguageConfigChange={onLanguageConfigChange}
            elevenLabsAvailable={elevenLabsAvailable}
          />
          <FlowVersions flowId={flowId} currentPublicationId={currentPublicationId} />
        </Group>

        <Group gap="sm" wrap="nowrap">
          <Button
            variant="subtle"
            leftSection={<IconDeviceFloppy size={16} />}
            onClick={onSave}
            loading={isSaving}
            disabled={!hasChanges}
          >
            Save
          </Button>
          {errorCount > 0 ? (
            <ErrorsButton issues={issues} errorCount={errorCount} onSelectNode={onSelectNode} />
          ) : (
            <Button
              color="green"
              leftSection={<IconRocket size={16} />}
              onClick={onPublish}
              loading={isPublishing}
              disabled={!canPublish}
            >
              Publish
            </Button>
          )}
        </Group>
      </Group>
    </Paper>
  );
}
